<template lang="pug">
  .px-8.pt-5.pb-10.bg-neutral-300(style="min-height: calc(100dvh - 72px)")
    div(style="max-width:1600px; margin: 0 auto;")
      //- Header section with title and date filter
      .d-flex.justify-space-between.align-center
        //- Panel claims title
        .yz-heading-5 {{ $t('panel.panel_claims') }}

        //- Date filter dropdown
        range-dropdown

      //- Main content area
      panel-stats.mt-4(ref="panelStats" :initialized="initialized")

      //- Claims table section
      yz-card.mt-4.px-5.py-4
        template(v-if="initialized")
          .d-flex.justify-space-between
            //- Tabs
            yz-tabs(v-model="activeTab")
              yz-tab(:label="$t('panel.tab_all')")
              yz-tab(:label="$t('panel.tab_pending')" :badge="invoicePendingCount")
              yz-tab(:label="$t('panel.tab_overdue')" :badge="invoiceOverdueCount")
              yz-tab(:label="$t('panel.tab_submitted')")
              yz-tab(:label="$t('panel.tab_approved')")
              yz-tab(:label="$t('panel.tab_rejected')")
              yz-tab(:label="$t('panel.tab_received')")
              yz-tab(:label="$t('panel.tab_cancelled')")

            // Actions
            .d-flex.align-center.gap-2
              yz-search-btn(v-model="searchText" size="lg" @search="onSearch")
              customize-column-menu(:type="customizeColumnType" :showTooltip="false" iconSize="21")
              //yz-btn(label="Download" size="lg" append-icon="$yzi-chevron-down")

          yz-divider

          // Claims Table Component
          claim-table(v-if="isClaimTab" ref="claimTable" :active-tab="activeTab" :search-text="searchText" :initial-page="currentPage" :initial-filters="initialFilters" @updated="onTableUpdated" @page-changed="onPageChanged" @filters-serialized="onFiltersSerialized")

          //- Invoice Table Component
          invoice-table(v-if="isInvoiceTab" ref="invoiceTable" :active-tab="activeTab" :search-text="searchText" :initial-page="currentPage" :initial-filters="initialFilters" @updated="onTableUpdated" @count-updated="onInvoiceCountUpdated" @page-changed="onPageChanged" @filters-serialized="onFiltersSerialized")

        template(v-else)
          yz-skeleton-loader(type="article@2")
</template>

<script>
import { mapGetters } from 'vuex'
import RangeDropdown from '@/components/clinic/panel/claims/RangeDropdown.vue'
import PanelStats from '@/components/clinic/panel/claims/PanelStats.vue'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import ClaimTable from '@/components/clinic/panel/claims/ClaimTable.vue'
import InvoiceTable from '@/components/clinic/panel/claims/InvoiceTable.vue'

export default {
  components: { RangeDropdown, CustomizeColumnMenu, ClaimTable, InvoiceTable, PanelStats },
  data() {
    return {
      activeTab: 0,
      searchText: null,
      initialized: false,
      // Invoice counts for badges
      invoicePendingCount: 0,
      invoiceOverdueCount: 0,
      // Pagination
      currentPage: 1,
      // Filters
      initialFilters: null,
      // Tab indices for claim tabs
      claimTabIndices: [0, 3, 4, 5, 6, 7],
      // Tab name to index mapping
      tabNameToIndex: {
        all: 0,
        pending: 1,
        overdue: 2,
        submitted: 3,
        approved: 4,
        rejected: 5,
        received: 6,
        cancelled: 7
      },
      // Tab index to name mapping
      tabIndexToName: ['all', 'pending', 'overdue', 'submitted', 'approved', 'rejected', 'received', 'cancelled']
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'clinic/getPanelClaimDates',
      beginDate: 'clinic/getPanelClaimBeginDate',
      untilDate: 'clinic/getPanelClaimUntilDate',
      currentDateRange: 'clinic/getPanelClaimCurrentDateRange'
    }),
    isClaimTab() {
      return this.claimTabIndices.includes(this.activeTab)
    },
    isInvoiceTab() {
      return [1, 2].includes(this.activeTab)
    },
    customizeColumnType() {
      return this.isInvoiceTab ? 'panel_claim_invoice' : 'panel_claim'
    }
  },
  async beforeMount() {
    await this.initializeDatesFromRoute()
    await this.initializeStateFromRoute()
    await this.fetchInvoiceCounts()
    this.initialized = true
  },
  watch: {
    activeTab(newTab, oldTab) {
      // Check if switching between different table types (claim ↔ invoice)
      const isOldTabClaim = this.claimTabIndices.includes(oldTab)
      const isNewTabClaim = this.claimTabIndices.includes(newTab)

      // Clear filters when switching between claim and invoice tables
      if (isOldTabClaim !== isNewTabClaim) {
        this.initialFilters = null
        // Remove filters from URL query
        const newQuery = { ...this.$route.query }
        delete newQuery.filters
        this.$router.replace({ query: newQuery }).catch(() => {})
      }

      // Reset page to 1 when changing tabs
      this.currentPage = 1
      this.updatePageQueryParam(1)
      this.updateRouteQuery(newTab)
    },
    '$route.query.tab': {
      handler(newTabName) {
        if (newTabName) {
          const tabIndex = this.tabNameToIndex[newTabName]
          if (tabIndex !== undefined && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      }
    },
    '$route.query.search': {
      handler(newSearchText) {
        // Sync searchText with URL query parameter
        const searchValue = newSearchText || null
        if (this.searchText !== searchValue) {
          this.searchText = searchValue
        }
      }
    },
    '$route.query.page': {
      handler(newPage) {
        // Sync currentPage with URL query parameter when browser back/forward
        const pageNumber = parseInt(newPage) || 1
        if (this.currentPage !== pageNumber) {
          this.currentPage = pageNumber
          // Update the child table component's page
          if (this.isClaimTab && this.$refs.claimTable) {
            this.$refs.claimTable.currentPage = pageNumber
          }
          if (this.isInvoiceTab && this.$refs.invoiceTable) {
            this.$refs.invoiceTable.currentPage = pageNumber
          }
        }
      }
    },
    '$route.query.dateRange': {
      handler(newDateRange) {
        // Sync date range with URL query parameter when browser back/forward
        if (newDateRange && this.$route.query.beginDate && this.$route.query.untilDate) {
          const currentRange = this.currentDateRange?.id
          if (currentRange !== newDateRange) {
            this.$store.commit('clinic/setPanelClaimBeginDate', this.$route.query.beginDate)
            this.$store.commit('clinic/setPanelClaimUntilDate', this.$route.query.untilDate)
            this.$store.commit('clinic/setPanelClaimDates', [this.$route.query.beginDate, this.$route.query.untilDate])
            const rangeOption = { id: newDateRange, name: this.getDateRangeName(newDateRange) }
            this.$store.commit('clinic/setPanelClaimCurrentDateRange', rangeOption)
          }
        }
      }
    },
    '$route.query.filters': {
      handler(newFilters) {
        // Sync filters with URL query parameter when browser back/forward
        if (newFilters !== this.initialFilters) {
          this.initialFilters = newFilters || null
          // Child components will reactively handle the prop change via their initialFilters watcher
        }
      }
    },
    dates() {
      if (!this.initialized) return

      this.fetchInvoiceCounts()
      this.updateDateQueryParams()
      this.fetchStats()
    },
    currentDateRange() {
      this.updateDateQueryParams()
    }
  },
  methods: {
    initializeStateFromRoute() {
      const tabName = this.$route.query.tab
      if (tabName && this.tabNameToIndex[tabName] !== undefined) {
        this.activeTab = this.tabNameToIndex[tabName]
      } else {
        // Default to 'all' tab if no valid query param
        this.activeTab = 0
        this.updateRouteQuery(0)
      }

      // Initialize searchText from route query
      const searchQuery = this.$route.query.search
      if (searchQuery) {
        this.searchText = searchQuery
      }

      // Initialize currentPage from route query
      const pageQuery = this.$route.query.page
      if (pageQuery) {
        this.currentPage = parseInt(pageQuery) || 1
      }

      // Initialize filters from route query
      const filtersQuery = this.$route.query.filters
      if (filtersQuery) {
        this.initialFilters = filtersQuery
      }
    },
    initializeDatesFromRoute() {
      const { dateRange, beginDate, untilDate } = this.$route.query

      // Only initialize from URL if all date params are present
      if (dateRange && beginDate && untilDate) {
        // Commit to Vuex store
        this.$store.commit('clinic/setPanelClaimBeginDate', beginDate)
        this.$store.commit('clinic/setPanelClaimUntilDate', untilDate)
        this.$store.commit('clinic/setPanelClaimDates', [beginDate, untilDate])

        // Find and set the current date range option
        const rangeOption = { id: dateRange, name: this.getDateRangeName(dateRange) }
        this.$store.commit('clinic/setPanelClaimCurrentDateRange', rangeOption)
      }
    },
    getDateRangeName(rangeId) {
      const rangeNames = {
        yesterday: this.$t('panel.yesterday'),
        lastSeven: this.$t('panel.last_7_days'),
        lastThirty: this.$t('panel.last_30_days'),
        lastNinety: this.$t('panel.last_90_days'),
        thisWeek: this.$t('panel.this_week'),
        thisMonth: this.$t('panel.this_month'),
        thisYear: this.$t('panel.this_year'),
        lastWeek: this.$t('panel.last_week'),
        lastMonth: this.$t('panel.last_month'),
        custom: this.$t('panel.custom')
      }
      return rangeNames[rangeId] || this.$t('panel.custom')
    },
    updateRouteQuery(tabIndex) {
      const tabName = this.tabIndexToName[tabIndex]
      if (tabName && this.$route.query.tab !== tabName) {
        this.$router
          .replace({
            query: { ...this.$route.query, tab: tabName }
          })
          .catch(() => {})
      }
    },
    updateDateQueryParams() {
      const newQuery = { ...this.$route.query }

      // Update date query params if dates are set
      if (this.beginDate && this.untilDate && this.currentDateRange?.id) {
        newQuery.dateRange = this.currentDateRange.id
        newQuery.beginDate = this.beginDate
        newQuery.untilDate = this.untilDate
      } else {
        // Remove date params if not set
        delete newQuery.dateRange
        delete newQuery.beginDate
        delete newQuery.untilDate
      }

      // Only update if query has changed
      const currentDateRange = this.$route.query.dateRange
      const currentBeginDate = this.$route.query.beginDate
      const currentUntilDate = this.$route.query.untilDate

      if (currentDateRange !== newQuery.dateRange || currentBeginDate !== newQuery.beginDate || currentUntilDate !== newQuery.untilDate) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    fetchStats() {
      if (this.$refs.panelStats) {
        this.$refs.panelStats.fetchStats()
      }
    },
    onTableUpdated() {
      this.fetchStats()
      this.fetchInvoiceCounts()
    },
    async fetchInvoiceCounts(filterParams = null) {
      try {
        const baseParams = { page_size: 1 }

        // Add date range filtering if dates are set
        if (this.beginDate && this.untilDate) {
          baseParams.begin = this.beginDate
          baseParams.until = this.untilDate
        }

        // Add search filter if searchText exists
        if (this.searchText) {
          baseParams.search = this.searchText
        }

        // Add filter params if provided
        if (filterParams && Object.keys(filterParams).length > 0) {
          Object.assign(baseParams, filterParams)
        }

        // Only fetch counts for tabs that are NOT currently active
        // Active tab count will be updated by InvoiceTable via onInvoiceCountUpdated
        const promises = []

        if (this.activeTab !== 1) {
          // Not on Pending tab, fetch pending count
          promises.push(
            this.$axios
              .get(`/panels/${this.company.slug}/invoices`, {
                params: { ...baseParams, to_submit: 'pending' }
              })
              .then(response => {
                this.invoicePendingCount = response.data.count || 0
              })
          )
        }

        if (this.activeTab !== 2) {
          // Not on Overdue tab, fetch overdue count
          promises.push(
            this.$axios
              .get(`/panels/${this.company.slug}/invoices`, {
                params: { ...baseParams, to_submit: 'overdue' }
              })
              .then(response => {
                this.invoiceOverdueCount = response.data.count || 0
              })
          )
        }

        await Promise.all(promises)
      } catch (error) {
        console.error('Error fetching invoice counts:', error)
      }
    },
    onSearch(query) {
      this.searchText = query

      // Update route query with search parameter
      const newQuery = { ...this.$route.query }
      if (query) {
        newQuery.search = query
      } else {
        delete newQuery.search
      }

      if (this.$route.query.search !== query) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }

      this.fetchInvoiceCounts()
      if (this.isClaimTab) {
        if (this.$refs.claimTable?.currentPage !== 1) {
          this.$refs.claimTable.currentPage = 1
          return
        }

        this.$refs.claimTable.fetchClaims()
      }
      if (this.isInvoiceTab) {
        if (this.$refs.invoiceTable?.currentPage !== 1) {
          this.$refs.invoiceTable.currentPage = 1
          return
        }

        this.$refs.invoiceTable.fetchInvoices()
      }
    },
    onInvoiceCountUpdated({ tab, count, filters }) {
      // Update invoice counts based on tab index
      if (tab === 1) {
        // Pending tab
        this.invoicePendingCount = count
      } else if (tab === 2) {
        // Overdue tab
        this.invoiceOverdueCount = count
      }

      // Update counts for other tabs with current filters
      this.fetchInvoiceCounts(filters)
    },
    onPageChanged(page) {
      this.currentPage = page
      this.updatePageQueryParam(page)
    },
    updatePageQueryParam(page) {
      const newQuery = { ...this.$route.query }

      // Update page query param
      if (page && page !== 1) {
        newQuery.page = page.toString()
      } else {
        // Remove page param if it's page 1 (default)
        delete newQuery.page
      }

      // Only update if query has changed
      if (this.$route.query.page !== newQuery.page) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    onFiltersSerialized(serialized) {
      this.updateFilterQueryParam(serialized)
    },
    updateFilterQueryParam(serializedFilters) {
      const newQuery = { ...this.$route.query }

      // Update filters query param
      if (serializedFilters) {
        newQuery.filters = serializedFilters
      } else {
        // Remove filters param if empty
        delete newQuery.filters
      }

      // Only update if query has changed
      if (this.$route.query.filters !== newQuery.filters) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    }
  }
}
</script>

<style scoped></style>
